import { Formik } from "formik";
import * as Yup from "yup";
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Select,
  InputNumber,
  Tag,
  Space,
} from "antd";
import { Calculator, Edit3, Trash2 } from "lucide-react";

const { TextArea } = Input;

// Validation schema — Formula/Unit/Min/Max Limit are the only user choices;
// Calculation Name/Code/Description/Formula Expression are read-only,
// snapshotted from whichever active Formula Library entry is picked below.
const validationSchema = Yup.object().shape({
  formula_definition_id: Yup.string().nullable().required("Formula is required"),
  unit_id: Yup.string().nullable().required("Unit is required"),
  min_value: Yup.number()
    .nullable()
    .typeError("Must be a number")
    .required("Min Limit is required"),
  max_value: Yup.number()
    .nullable()
    .typeError("Must be a number")
    .required("Max Limit is required")
    .moreThan(Yup.ref("min_value"), "Max Limit must be greater than Min Limit"),
});

const ApiCalculationForm = ({
  editIndex,
  onSubmit = (values) => console.log("API Calc Values:", values),
  formula_definition_list = [],
  unit_list = [],
  values = {
    formula_definition_id: null,
    formula_code: "",
    formula_name: "",
    formula_description: "",
    formula_expression: "",
    // Variable names ("keys") the formula's expression references, e.g.
    // ["A", "B", "C"] for "A+B+C*100" — snapshotted from the Formula
    // Definition's own `detected_variables` (same value Formula Library's
    // "Check Expression" shows). The batch-execution app (separate repo)
    // reads this to know which manual-entry fields to render for the
    // operator.
    formula_variables: [],
    unit_id: null,
    unit: "",
    min_value: null,
    max_value: null,
  },
}) => {
  return (
    <Formik
      initialValues={values}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({ values, errors, touched, handleSubmit, setFieldValue }) => (
        <Form layout="vertical" onFinish={handleSubmit}>
          <Row gutter={24}>
            <Col span={24}>
              <Form.Item
                label="Formula"
                validateStatus={
                  touched.formula_definition_id && errors.formula_definition_id ? "error" : ""
                }
                help={touched.formula_definition_id && errors.formula_definition_id}
              >
                <Select
                  placeholder="Select an active formula from Formula Library"
                  value={values.formula_definition_id || null}
                  showSearch
                  allowClear
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  notFoundContent="No active formulas — create and activate one in Configuration → Formula"
                  onChange={(val, option) => {
                    const f = option?.formula;
                    setFieldValue("formula_definition_id", val || "");
                    setFieldValue("formula_code", f?.code || "");
                    setFieldValue("formula_name", f?.name || "");
                    setFieldValue("formula_description", f?.description || "");
                    setFieldValue("formula_expression", f?.expression || "");
                    setFieldValue("formula_variables", f?.detected_variables || []);
                  }}
                  options={formula_definition_list?.map((f) => ({
                    value: f.id,
                    label: `${f.code} — ${f.name}`,
                    formula: f,
                  }))}
                />
              </Form.Item>
            </Col>

            {values.formula_definition_id && (
              <>
                <Col span={8}>
                  <Form.Item label="Calculation Name">
                    <Input value={values.formula_name} disabled />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Calculation Code">
                    <Input value={values.formula_code} disabled />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Description">
                    <Input value={values.formula_description} disabled title={values.formula_description} />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item label="Formula Expression">
                    <TextArea
                      rows={2}
                      value={values.formula_expression}
                      disabled
                      style={{ fontFamily: "monospace", fontSize: 13 }}
                    />
                  </Form.Item>
                </Col>
                {!!values.formula_variables?.length && (
                  <Col span={24}>
                    <Form.Item label="Operator will manually enter these values during batch execution">
                      <Space size={[4, 4]} wrap>
                        {values.formula_variables.map((v) => (
                          <Tag key={v} color="blue">{v}</Tag>
                        ))}
                      </Space>
                    </Form.Item>
                  </Col>
                )}
              </>
            )}

            <Col span={8}>
              <Form.Item
                label="Unit"
                validateStatus={
                  touched.unit_id && errors.unit_id ? "error" : ""
                }
                help={touched.unit_id && errors.unit_id}
              >
                <Select
                  placeholder="Select unit"
                  value={values.unit_id || null}
                  onChange={(val, option) => {
                    setFieldValue("unit_id", val || "");
                    if (val) {
                      setFieldValue("unit", option.label);
                    }
                  }}
                  showSearch
                  allowClear
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  options={unit_list?.map((unit) => ({
                    value: unit.id,
                    label: unit.unit_name,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Min Limit"
                validateStatus={
                  touched.min_value && errors.min_value ? "error" : ""
                }
                help={touched.min_value && errors.min_value}
              >
                <InputNumber
                  className="w-full"
                  placeholder="Min Limit"
                  value={values.min_value}
                  onChange={(val) => setFieldValue("min_value", val)}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Max Limit"
                validateStatus={
                  touched.max_value && errors.max_value ? "error" : ""
                }
                help={touched.max_value && errors.max_value}
              >
                <InputNumber
                  className="w-full"
                  placeholder="Max Limit"
                  value={values.max_value}
                  onChange={(val) => setFieldValue("max_value", val)}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Button type="primary" htmlType="submit" block>
                {editIndex !== null ? "Update" : "Add"}
              </Button>
            </Col>
          </Row>
        </Form>
      )}
    </Formik>
  );
};

export default ApiCalculationForm;

export const ApiCalculationCard = ({ fromView, item, onEdit, onDelete }) => {
  const { formula_name, formula_code, formula_expression, formula_variables, unit, min_value, max_value } = item;

  const showLabel = (label, code) => label || (code ? "Code: " + code : "N/A");

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Calculator className="w-4 h-4 text-blue-600" />
          </div>
          <div>
            <p
              className="font-semibold text-gray-800 leading-tight truncate"
              title={showLabel(formula_name, formula_code)}
            >
              {showLabel(formula_name, formula_code)}
            </p>
            {formula_code && (
              <p className="text-xs text-gray-600 truncate" title={`Code: ${formula_code}`}>
                Code: {formula_code}
              </p>
            )}
          </div>
        </div>

        {/* Actions — hidden in read-only (fromView) contexts, e.g. rule-locked items */}
        {!fromView && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              className="p-1.5 rounded-md hover:bg-blue-50 transition-colors border border-transparent hover:border-blue-200"
              onClick={() => onEdit()}
              title="Edit"
            >
              <Edit3 className="w-4 h-4 text-blue-600" />
            </button>
            <button
              type="button"
              className="p-1.5 rounded-md hover:bg-red-50 transition-colors border border-transparent hover:border-red-200"
              onClick={() => onDelete()}
              title="Delete"
            >
              <Trash2 className="w-4 h-4 text-red-500" />
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="grid grid-cols-2 gap-2">
        {unit && (
          <div className="bg-indigo-50 rounded-lg p-2">
            <p className="text-xs text-indigo-700 font-medium">Unit</p>
            <p className="text-indigo-900 text-sm truncate" title={unit}>
              {unit}
            </p>
          </div>
        )}

        {(min_value !== null || max_value !== null) && (
          <div className="bg-orange-50 rounded-lg p-2">
            <p className="text-xs text-orange-700 font-medium">Range</p>
            <p
              className="text-orange-900 text-sm truncate"
              title={`${min_value} - ${max_value} ${unit}`}
            >
              {min_value !== null ? min_value : "N/A"} -{" "}
              {max_value !== null ? max_value : "N/A"}{" "}
              <span className="text-xs">{unit}</span>
            </p>
          </div>
        )}
      </div>

      {formula_expression && (
        <div className="mt-3 bg-gray-50 rounded-lg p-2">
          <p className="text-xs text-gray-600 font-medium">Formula</p>
          <p
            className="text-gray-700 font-mono text-sm truncate"
            title={formula_expression}
          >
            {formula_expression}
          </p>
        </div>
      )}

      {!!formula_variables?.length && (
        <div className="mt-2 bg-blue-50 rounded-lg p-2">
          <p className="text-xs text-blue-700 font-medium mb-1">Operator enters</p>
          <div className="flex flex-wrap gap-1">
            {formula_variables.map((v) => (
              <Tag key={v} color="blue" style={{ marginInlineEnd: 0 }}>{v}</Tag>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
